import { get, set } from 'local-storage'
import {
  IColor,
  ICompletedContextState,
  IModel,
  ITexture,
  IVariant,
} from '../../type/modelType'
import { models } from '../../../_mocks_/models'
import { GLTFType } from '../../type/GLTFType'
import { IMesh } from '../../type/modelType'
import { v4 as uuidv4 } from 'uuid'
import {
  applyDesignToModel,
  loadDesignState,
  modelToInitData,
  removeDesignStatesInModel,
  saveDesignState,
} from '../design/saveLoad'
import { extractJsonString } from '../utils'
import {
  createProductPackage,
  deleteProduct,
  deleteProductVariant,
  deleteVariant,
  getProductPackages,
  ICreatedProductWithVariant,
  updateProductPackage,
  updateVariantOptions,
} from './product'
import { VariantType } from '../../type/backendType'

const storageKey = 'models'

export const listModelsStorge = (): IModel[] => {
  return get<IModel[]>(storageKey) ?? []
}

export const listModels = (context: ICompletedContextState): IModel[] => {
  return getProductPackages(context).map(pp => JSON.parse(pp.meta)) ?? []
}

export const loadModelConfig = async (
  modelId: string,
  context: ICompletedContextState
): Promise<IModel> => {
  const storage = listModels(context).find(m => m && m.id === modelId)

  if (storage) {
    return storage
  }

  const model = models.find(m => m.id === modelId) as IModel

  await saveModelConfig(model, context)

  return model
}

export const saveModelConfig = async (
  model: IModel,
  context: ICompletedContextState
) => {
  const models = listModels(context)
  const existingModel = models.find(m => m && m.id === model.id)
  let productPackageId = ''
  let products: ICreatedProductWithVariant[] = []

  if (!existingModel && !model.productPackageId) {
    const createdProductPackage = await createProductPackage(model, context)
    productPackageId = createdProductPackage.id
    products = createdProductPackage.products ?? []
  } else if (existingModel) {
    productPackageId = existingModel.productPackageId ?? ''
    await Promise.all(
      existingModel?.meshes.map(async mesh => {
        if (mesh.meshVariantId) {
          return await updateVariantOptions(
            mesh.meshVariantId,
            mesh.variants,
            context.shop
          )
        }
      })
    )
  }

  const newModel = {
    ...model,
    ...(productPackageId !== '' && {
      productPackageId,
    }),
    meshes: model.meshes.map(mesh => {
      const product = products.find(p => p.meshId === mesh.id)
      return {
        ...mesh,
        ...(product && {
          meshVariantId: product.meshVariantId,
          productId: product.id,
        }),
      }
    }),
  }

  const data: IModel[] = [
    ...models.filter(m => m && m.id !== model.id),
    ...[newModel],
  ]

  await updateProductPackage(productPackageId, {
    title: {
      id: uuidv4(),
      text: [
        {
          langCode: context.shop.defaultLanguage,
          content: model.title,
        },
      ],
    },
    meta: JSON.stringify(newModel),
  })

  set<IModel[]>(storageKey, data)
}

export const readGltf = async (uri: string): Promise<GLTFType> => {
  return new Promise((resolve, reject) => {
    fetch(uri)
      .then(data => {
        return data.text().then(text => JSON.parse(extractJsonString(text)))
      })
      .then((json: GLTFType) => {
        resolve(json)
      })
      .catch(e => {
        throw e
      })
  })
}

export const getMeshGroups = (
  gltf: GLTFType,
  currentMeshes?: IMesh[]
): IMesh[] => {
  const meshes: IMesh[] = []

  const gltfNodes = gltf.nodes.filter((node, index) =>
    gltf.scenes[0].nodes.includes(index)
  )

  gltfNodes.forEach(gtlfMesh => {
    const [groupName, no, name] = gtlfMesh.name.split('-')

    const currentMeshGroup = currentMeshes?.find(m => m.meshGroup === groupName)
    const currentMeshData = currentMeshGroup?.variants.find(
      m => m.meshName === gtlfMesh.name
    )

    const meshIndex = meshes.findIndex(m => m.meshGroup === groupName)

    if (meshIndex > -1) {
      meshes[meshIndex].variants.push({
        id: currentMeshData?.id ?? uuidv4(),
        title: currentMeshData?.title ?? name,
        variantType: VariantType.MESH,
        meshName: gtlfMesh.name,
        ...(currentMeshData?.default && {
          default: true,
        }),
        ...(currentMeshData?.isOption && {
          isOption: true,
        }),
      })
    } else {
      meshes.push({
        id: currentMeshGroup?.id ?? uuidv4(),
        title: currentMeshGroup?.title ?? groupName,
        meshGroup: groupName,
        ...(currentMeshGroup?.meshVariantId && {
          meshVariantId: currentMeshGroup.meshVariantId,
        }),
        ...(currentMeshGroup?.productId && {
          productId: currentMeshGroup.productId,
        }),
        ...(currentMeshGroup?.canHideMesh && {
          canHideMesh: currentMeshGroup.canHideMesh,
        }),
        variants: [
          {
            id: currentMeshData?.id ?? uuidv4(),
            title: currentMeshData?.title ?? name,
            variantType: VariantType.MESH,
            meshName: gtlfMesh.name,
            ...(currentMeshData?.default && {
              default: true,
            }),
            ...(currentMeshData?.isOption && {
              isOption: true,
            }),
          },
        ],
        ...(currentMeshGroup?.colors && {
          colors: currentMeshGroup.colors,
        }),
        ...(currentMeshGroup?.textures && {
          textures: currentMeshGroup.textures,
        }),
      })
    }
  })

  return meshes
}

export const mergeMeshesSettings = (
  currentMeshes: IMesh[],
  readMeshes: IMesh[]
): IMesh[] => {
  const merged = readMeshes.map(mesh => {
    const paired = currentMeshes.find(m => m.id === mesh.id)
    if (paired) {
      return {
        ...mesh,
        ...(paired.colors && { colors: paired.colors }),
        ...(paired.textures && { textures: paired.textures }),
        ...(paired.meshVariantId && { meshVariantId: paired.meshVariantId }),
        ...(paired.productId && { productId: paired.productId }),
        ...(paired.canHideMesh && { canHideMesh: paired.canHideMesh }),
      }
    }

    return mesh
  })

  return merged
}

export const switchToConfigurationMode = (model: IModel) => {
  const initData = modelToInitData(model)
  applyDesignToModel(model, initData, true)
}

export const switchToDesignMode = async (
  modelId: string,
  designId: string,
  context: ICompletedContextState
) => {
  const model = await loadModelConfig(modelId, context)
  const design = await loadDesignState(designId, context)

  if (model && design) {
    applyDesignToModel(model, design.modelConfig)
  }
}

export const colorToMeshConfiguration = (color: IColor, mesh: IMesh): IMesh => {
  let colors = mesh.colors?.filter(c => c.id !== color.id) ?? []

  if (color.default) {
    colors = colors.map(c => ({ ...c, default: false }))
  }

  return {
    ...mesh,
    colors: [...colors, color],
    ...(color.default &&
      mesh.textures && {
        textures: mesh.textures.map(t => ({ ...t, default: false })),
      }),
  }
}

export const textureToMeshConfiguration = (
  texture: ITexture,
  mesh: IMesh
): IMesh => {
  let textures = mesh.textures?.filter(t => t.id !== texture.id) ?? []

  if (texture.default) {
    textures = textures.map(t => ({ ...t, default: false }))
  }

  return {
    ...mesh,
    textures: [...textures, texture],
    ...(texture.default &&
      mesh.colors && {
        colors: mesh.colors.map(c => ({ ...c, default: false })),
      }),
  }
}

export const variantToMeshConfiguration = (
  variant: IVariant,
  mesh: IMesh
): IMesh => {
  let variants = mesh.variants?.filter(v => v.id !== variant.id) ?? []

  if (variant.default) {
    variants = variants.map(v => ({ ...v, default: false }))
  }

  return { ...mesh, variants: [...variants, variant] }
}

export const meshToModelConfiguration = (
  mesh: IMesh,
  model: IModel
): IModel => {
  const meshes = model.meshes?.filter(m => m.id !== mesh.id) ?? []

  return { ...model, meshes: [...meshes, mesh] }
}

export const deleteModel = async (
  modelId: string,
  context: ICompletedContextState,
  purge = true
) => {
  const storage = listModels(context).find(m => m.id === modelId)

  if (storage) {
    if (purge) {
      await Promise.all(
        storage.meshes.map(async mesh => {
          if (mesh.productId) {
            if (mesh.meshVariantId) {
              await deleteProductVariant(mesh.meshVariantId, mesh.productId)
              await deleteVariant(mesh.meshVariantId)
            }

            await deleteProduct(mesh.productId)
          }
        })
      )

      if (storage.productPackageId) {
        await deleteProduct(storage.productPackageId)
      }
    }

    // const data: IModel[] = [...models.filter(m => m.id !== modelId)]

    // set<IModel[]>(storageKey, data)

    removeDesignStatesInModel(modelId)

    await context.fetchShop()
  }
}
